<template>
  <div class="row">
    <div class="col-md-5 col-xs-12">
      <div class="ibox">
        <div class="ibox-content">
          <h1 class="font-heavy p-t-md m-t-none m-b-none hidden-xs">Renew your subscription</h1>
          <p class="p-t-sm p-t-m-only-xs">Extend your subscription before it <strong>{{
              expiresOn()
                  .toLowerCase()
            }}</strong> to avoid missing any training.</p>
          <div
              class="m-t-lg bg-gray-50 border-bottom border-top p-y-m font-semi text-gray-500 uppercase tracking-wide m-t-m"
              style="margin-left: -24px; margin-right: -24px; padding-left: 24px; padding-right: 24px;">
            Renewal Details
          </div>
          <div class="flex flex-col gap-y-m">
            <div v-if="showPicker" class="m-t-sm">
              <p class="text-16 font-semi m-b-xs">
                Choose your renewal tier
              </p>
              <p class="text-12 text-gray-600 m-t-none m-b-sm">
                {{ productNames() }} offers more than one package length. Pick the one you'd like to renew on.
              </p>
              <div class="flex flex-col gap-y-xs">
                <label
                  v-for="plan in availablePlans"
                  :key="plan.id"
                  class="flex align-items-center gap-x-sm p-sm rounded-xs cursor-pointer"
                  :class="selectedPlanId === plan.id ? 'bg-blue-50 border border-solid border-blue-600' : 'border border-solid border-gray-200'"
                >
                  <input type="radio" :value="plan.id" v-model="selectedPlanId" name="renewal_plan_id" />
                  <span class="flex-grow-1">
                    <span class="block font-semi text-14">{{ plan.name }}</span>
                    <span class="block text-12 text-gray-600">{{ planLengthFor(plan) }} commitment</span>
                  </span>
                  <span class="text-14 font-semi">{{ formatAsCurrency(plan.amount) }}/{{ plan.interval }}</span>
                </label>
              </div>
            </div>
            <div class="flex justify-space-between m-t-sm">
              <div>
                <p class="text-16 font-semi m-b-none">
                  {{ productNames() }} - {{ selectedPlan.name }}
                </p>
                <p class="m-t-none m-b-none text-10">
                  Total includes WIQ fees <span v-if="coupon && !couponIsExpired">and {{ coupon.code }} discount</span>
                </p>
              </div>
              <p class="text-16 flex-shrink-1">
                {{ formatAsCurrency(renewalTotal) }} {{ planPriceExtra(selectedPlan) }}
              </p>
            </div>
            <div v-if="couponIsExpired" class="bg-red-50 text-red-900 p-sm rounded-xxs">
              <p class="m-t-none m-b-none">
                The coupon <strong>{{ coupon.code }}</strong> is deactivated and will not be applied to your renewal. Please reach out to a coach or admin if you think this is a mistake.
              </p>

            </div>
            <div class="flex justify-space-between">
              <p class="text-16 font-semi">Wrestler</p>
              <p class="text-16 flex-shink-1">{{ wrestlerNames() }}</p>
            </div>
            <div class="flex justify-space-between">
              <p class="text-16 font-semi">Renewal Length</p>
              <p class="text-16 flex-shink-1">{{ planLength }}</p>
            </div>
          </div>
          <hr class="m-t-sm">
          <div class="bg-yellow-50 text-yellow-900 p-sm rounded-xxs">
            <p class="m-t-none m-b-none">
              By clicking Renew Subscription, <strong>you are agreeing to extend your auto-pay commitment by an
              additional {{ planLength }}<span v-if="newExpirationDate">, through {{ newExpirationDate }}</span></strong>.
            </p>
            <p class="m-t-sm" v-if="!needsPaymentMethod">You will not be charged until your normal billing date of the {{ billingDay }}.</p>
            <p class="m-t-sm" v-else>We'll save the card below now and charge it on your normal billing date of the {{ billingDay }}.</p>
          </div>

          <!-- No card on file (e.g. an imported membership): collect one before renewal is allowed. -->
          <div v-if="showCardForm" class="m-t-m">
            <div class="bg-blue-50 text-blue-900 p-sm rounded-xxs m-b-sm">
              <p class="m-t-none m-b-none">
                We don't have a card on file for this membership yet. Please add one below to renew.
              </p>
            </div>
            <update-cc
              :should-redirect="false"
              :force-reusable-card="true"
              force-reusable-card-reason="This card will be saved and used for your auto-pay renewal."
              :billing-partner="billingPartner"
              :billing-key="billingKey"
              :billing-acct-id="billingAcctId"
              :billing-intent-secret="billingIntentSecret"
              :team-name="teamName"
              :team-currency="teamCurrency"
              :billing-profile="billingProfile"
              :use-google-autocomplete="true"
              @card-updated="onCardAdded"
            ></update-cc>
          </div>

          <ladda-button v-if="renewReady" @lbClicked="renew" el-class="btn btn-primary m-t-sm w-full" :loading="loading">
            Renew Subscription
          </ladda-button>
          <div v-if="error">
            <small>
              <span class="text-danger">{{ errorMessage }}</span>
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { billingPlanMix } from './mixins/billing_plan_mix';
import { formattingMixin } from './mixins/formatting_mix';
import { errorableMix } from './mixins/errorable_mix';
import LaddaButton from '../../shared/ladda_button.vue';
import UpdateCc from './update_cc.vue';

export default {
  name: 'renew-subscription',
  components: { LaddaButton, UpdateCc },
  mixins: [billingPlanMix, formattingMixin, errorableMix],
  props: {
    subscription: {
      type: Object,
      required: true,
    },
    renewalPlan: {
      type: Object,
      requird: true,
    },
    renewalFee: {
      type: Object,
      default() {
        return {
          amount: 0,
        };
      },
    },
    renewalTax: {
      type: Object,
      default() {
        return {
          amount: 0,
        };
      },
    },
    // Alternate tiers the parent can pick from. Each entry: { id, name, amount, interval, interval_count,
    // intervals_to_bill, associated_fee_amount, associated_tax_amount }. Empty array hides the picker.
    availablePlans: {
      type: Array,
      default() {
        return [];
      },
    },
    // True when there is no payment method on file (e.g. an imported subscription). When true we
    // require the parent to add a card via <update-cc> before the renewal can be submitted.
    needsPaymentMethod: {
      type: Boolean,
      default: false,
    },
    // Passed through to <update-cc> (and its billing_partner_mix) only when needsPaymentMethod.
    billingPartner: {
      type: String,
      default: 'stripe',
    },
    billingKey: {
      type: String,
      default: '',
    },
    billingAcctId: {
      type: String,
      default: '',
    },
    billingIntentSecret: {
      type: String,
      default: '',
    },
    teamName: {
      type: String,
      default: '',
    },
    teamCurrency: {
      type: String,
      default: 'usd',
    },
    billingProfile: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    return {
      loading: false,
      selectedPlanId: this.renewalPlan ? this.renewalPlan.id : null,
      cardAdded: false,
    };
  },
  computed: {
    coupon() {
      return _.head(this.subscription.coupons);
    },
    couponIsExpired() {
      const { coupon } = this;
      if (!coupon) {
        return false;
      }

      if (!coupon.active || coupon.archived) {
        return true;
      }

      return false;
    },
    showPicker() {
      return this.availablePlans && this.availablePlans.length > 1;
    },
    // Show the card collection form until a card has been successfully saved.
    showCardForm() {
      return this.needsPaymentMethod && !this.cardAdded;
    },
    // The renewal can only be submitted once we have a card on file.
    renewReady() {
      return !this.needsPaymentMethod || this.cardAdded;
    },
    selectedPlan() {
      if (!this.showPicker) {
        return this.renewalPlan;
      }
      return _.find(this.availablePlans, { id: this.selectedPlanId }) || this.renewalPlan;
    },
    // Used by billingPlanMix helpers; mirrors selectedPlan so downstream formatting works for the chosen tier.
    billingPlan() {
      return this.selectedPlan;
    },
    pickedAlternate() {
      return this.showPicker && this.selectedPlanId !== this.renewalPlan.id;
    },
    effectiveFeeAmount() {
      if (this.pickedAlternate) {
        return this.selectedPlan.associated_fee_amount || 0;
      }
      return this.renewalFee.amount;
    },
    effectiveTaxAmount() {
      if (this.pickedAlternate) {
        return this.selectedPlan.associated_tax_amount || 0;
      }
      return this.renewalTax.amount;
    },
    planLength() {
      return this.planLengthFor(this.selectedPlan);
    },
    newExpirationDate() {
      if (!this.subscription.expires_on || !this.selectedPlan) {
        return '';
      }
      const cycles = this.selectedPlan.intervals_to_bill * this.selectedPlan.interval_count;
      const unitMap = { month: 'months', year: 'years', week: 'weeks' };
      const unit = unitMap[this.selectedPlan.interval] || 'months';
      return moment(this.subscription.expires_on).add(cycles, unit).format('MMM D, YYYY');
    },
    billingDay() {
      return moment(this.subscription.current_period_end_at)
        .format('Do');
    },
    renewalTotal() {
      // TODO fix this up if we ever get into quantity > 1 for renewals
      let total = this.selectedPlan.amount + this.effectiveFeeAmount + this.effectiveTaxAmount;
      if (this.coupon && !this.couponIsExpired) {
        if (this.coupon.amount_off > 0) {
          total -= this.coupon.amount_off;
        } else {
          total -= this.coupon.percent_off * (total);
        }
      }

      return total;
    },
  },
  methods: {
    planLengthFor(plan) {
      if (!plan) {
        return '';
      }

      const total = plan.interval_count * plan.intervals_to_bill;
      const pluralizedInterval = total > 1 ? `${plan.interval}s` : plan.interval;
      return `${total} ${pluralizedInterval}`;
    },
    onCardAdded() {
      this.cardAdded = true;
      this.$notificationManager.emit('show-toast', 'Card saved. You can now renew.', true);
    },
    renew: _.throttle(function () {
      const vm = this;
      if (vm.loading === true) {
        return;
      }

      // Guard: a card must be on file before we let the renewal go through.
      if (!vm.renewReady) {
        return;
      }

      vm.error = false;
      vm.loading = true;

      const renewUrl = vm.$apiService.renewSubscriptionUrl(vm.subscription.id);
      const params = vm.pickedAlternate
        ? { billing_subscription: { renew: { plan_id: vm.selectedPlanId } } }
        : {};
      axios.post(renewUrl, params)
        .then((response) => {
          vm.$notificationManager.emit('show-toast', 'Subscription renewed!', true);
          // Leave loading on until the redirect so the button can't fire a second renewal.
          setTimeout(() => {
            window.location.assign(`/subscriptions/${vm.subscription.billing_partner_id}`);
          }, 750);
        })
        .catch((error) => {
          // Surface the server's reason (e.g. "isn't up for renewal yet" from a stale tab or the Back button) so
          // a parent isn't told to try again on a renewal that already went through.
          const serverMessage = error.response?.data?.errors?.[1];
          vm.errorMessage = serverMessage || 'Something went wrong renewing your subscription. Try again or email support@wrestlingiq.com';
          vm.error = true;
          vm.loading = false;
        });
    }, 500, { trailing: false }),
  },
};
</script>
